import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Loader from './Components/Loader';
import Header from './Components/Header';
import MapMapbox from './Components/MapMapbox';
import Controls from './Components/MapControls';
import Filter from './Components/Filter';
import Sidebar from './Components/Sidebar';
import Logos from './Components/Logos';
import CookieConsent from './Components/CookieConsent';

import * as api from './Utilities/API';
import { gsheetToJSON, gsheetToObject, convertToGeoJSON, processPointData, useFocus } from './Utilities/Helpers';
import { DEFAULT_LANG } from './Utilities/Config';
import { MapProvider } from './Services/MapContext';

import './App.css';

export default function App() {
	// Map data
	const [data, setData] = useState(null);
	const [selectedData, setSelectedData] = useState(null);
	const [activeCategories, setActiveCategories] = useState(null);

	// Language
	const [language, setLanguage] = useState(null);
	const [availableLanguages, setAvailableLanguages] = useState(null);
	const [activeLanguage, setActiveLanguage] = useState(null);

	// Query search parameters
	const [searchParams, setSearchParams] = useSearchParams();

	// Windows/forms
	const [searchFocused] = useFocus();
	const [loaderVisible, setLoaderVisible] = useState(true);
	const [filterVisible, setFilterVisible] = useState(true);
	const [sidebarVisible, setSidebarVisible] = useState(false);
	const [sidebarWidth, setSidebarWidth] = useState(0);
	const handleLoaderVisibility = (state) => { setLoaderVisible(state); }
	const handleFilterVisibility = () => { setFilterVisible((prevState) => !prevState); }
	const handleSidebarVisibility = (state) => { setSidebarVisible(state); }

	// Cookie Consent
	const [disclaimerAgreed, setDisclaimerAgreed] = useState(JSON.parse(localStorage.getItem('disclaimerAgreed')) || false);
	const handleDisclaimerAgreed = (state) => { setDisclaimerAgreed(state); }

	// Set local storage item for the disclaimer
	useEffect(() => {
	 	localStorage.setItem('disclaimerAgreed', JSON.stringify(disclaimerAgreed));
	}, [disclaimerAgreed]);

	// Load map data
	useEffect(() => {
		const fetchLanguage = async () => {
			const lang = searchParams.get('lang') || DEFAULT_LANG;
			const response = await api.getTranslation();
			const json = gsheetToJSON(response);

			let translation = json.reduce((acc, val) => {
				if (val.param) acc[val.param] = val[`translation_${lang}`];
				return acc;
			}, {});
			
			// add translation
			const langArray = () => {
				let object = {};
				Object.entries(json.filter(val => val.param === 'language:name')[0]).forEach((el) => {
					const key = el[0].replace('translation_','');
					// filter out languages where text doesnt exist
					const dataExists = json.filter(el => el.param === 'map:title')[0][`translation_${key}`];
					if (el[0] !== 'param' && dataExists) {
						object[key] = el[1];
					}
				});
				return object;
			}

			setActiveLanguage(lang);
			setLanguage(translation);
			setAvailableLanguages(langArray());

			return translation;
		}

		const jumpToFeature = (data) => {
			// Get URL parameter for language
			const selectedId = searchParams.get('id') || null;
			if (selectedId) {
				const filteredFeatures = data.features.filter((el => el.id === selectedId));
				if (filteredFeatures.length > 0) {
					let foundFeature = filteredFeatures[0];
					if (foundFeature) {
						setSelectedData(foundFeature);
						// setSidebarVisible(true);
					}
				}
			}
		}

		const fetchData = async (lang, translation) => {
			const rawData = await api.getData(lang);

			const subjectTypes = gsheetToObject(rawData[0], true);
			setActiveCategories(subjectTypes);

			const pointData = processPointData(rawData[1], subjectTypes);

			// preload marker logos
			pointData.forEach(el => {
				if (!el.marker_thumbnail) return;
				const img = new Image();
    			img.src = el.marker_thumbnail;
			})
			
			// add translations
			let storiesLanguage = gsheetToObject(rawData[2] ? rawData[2] : rawData[1]);
			translation.stories = storiesLanguage;

			// add story translations
			setLanguage(translation);

			const data = convertToGeoJSON(pointData);
			setData(data);
			jumpToFeature(data);
		}

		const fetchAll = async () => {
			const lang = searchParams.get('lang') || DEFAULT_LANG;
			const translation = await fetchLanguage();
			await fetchData(lang, translation);
			setLoaderVisible(false);
		}

		fetchAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="App">
			<Loader
				show={loaderVisible}
			/>
			<Header
				language={language}
				availableLanguages={availableLanguages}
				searchParams={searchParams}
			/>

			{/*
			<Info
				show={infoVisible}
				onToggle={handleInfoVisibility}
				language={language}
			/>
			<IntroWindow
				show={introVisible}
				onToggle={handleIntroVisibility}
				language={language}
			/>
			*/}
			
			{!disclaimerAgreed &&
				<CookieConsent
					language={language}
					handleDisclaimerAgreed={handleDisclaimerAgreed}
					setLoaderVisible={handleLoaderVisibility}
				/>
			}

			{disclaimerAgreed &&
				<>
					<MapProvider>
						<Controls
							data={data}
							language={language}
							searchFocused={searchFocused}
							setSelectedData={setSelectedData}
							onToggleFilter={handleFilterVisibility}
						/>
						<MapMapbox
							data={data}
							activeCategories={activeCategories}
							selectedData={selectedData}
							setSelectedData={setSelectedData}
							toggleSidebar={handleSidebarVisibility}
							setLoaderVisible={setLoaderVisible}
							setSearchParams={setSearchParams}
							sidebarWidth={sidebarWidth}
						/>
					</MapProvider>
					<Sidebar
						show={sidebarVisible}
						data={selectedData}
						categories={activeCategories}
						onToggle={handleSidebarVisibility}
						language={language}
						setSelectedData={setSelectedData}
						setSearchParams={setSearchParams}
						setSidebarWidth={setSidebarWidth}
					/>
				</>
			}
			<Filter
				categories={activeCategories}
				setActiveCategories={setActiveCategories}
				show={filterVisible}
				onToggle={handleFilterVisibility}
				activeLanguage={activeLanguage}
				language={language}
			/>
			<Logos/>
		</div>
	);
}
